import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import type { UseFormRegister, UseFormWatch, Control, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  PlusIcon,
  TrashIcon,
  Bars3Icon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import { useTemplate, useCreateTemplate, useUpdateTemplate } from '../../hooks/useSurveys';
import type { QuestionType } from '../../types';

const questionTypes: { value: QuestionType; label: string; description: string }[] = [
  { value: 'csat_score', label: 'CSAT Score (1-5)', description: 'CSAT satisfaction scale' },
  { value: 'scale', label: 'Scale (1-5)', description: 'Numeric scale rating' },
  { value: 'rating', label: 'Star Rating', description: 'Star-based rating' },
  { value: 'star', label: 'Star Rating (DB)', description: 'Star-based rating' },
  { value: 'single_choice', label: 'Single Choice', description: 'Select one option' },
  { value: 'multi_choice', label: 'Multi Choice', description: 'Select multiple options' },
  { value: 'text', label: 'Text', description: 'Free text response' },
  { value: 'nps_score', label: 'NPS Score (1-10)', description: 'Net Promoter Score scale' },
  { value: 'yes_no', label: 'Yes/No', description: 'Simple yes or no question' },
];

const questionSchema = z.object({
  question_text: z.string().min(1, 'Question text is required'),
  question_type: z.enum(['csat_score', 'nps_score', 'text', 'single_choice', 'multi_choice', 'rating', 'scale', 'star', 'yes_no']),
  options: z.array(z.string()).nullable().optional(),
  is_required: z.boolean().optional().default(true),
  position: z.number().optional(),
});

const templateSchema = z.object({
  name: z.string().min(1, 'Template name is required').max(200),
  description: z.string().optional(),
  status: z.enum(['draft', 'active', 'archived']).optional(),
  questions: z.array(questionSchema).min(1, 'At least one question is required'),
  email_subject: z.string().optional(),
  email_body: z.string().optional(),
  sender_name: z.string().optional(),
});

type TemplateFormData = z.infer<typeof templateSchema>;

export default function TemplateBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: existingTemplate, isLoading } = useTemplate(id || '');
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate(id || '');

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<TemplateFormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: '',
      description: '',
      status: 'draft',
      questions: [
        {
          question_text: '',
          question_type: 'csat_score',
          options: null,
          is_required: true,
          position: 0,
        },
      ],
      email_subject: '',
      email_body: '',
      sender_name: '',
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'questions',
  });

  useEffect(() => {
    if (existingTemplate && isEditing) {
      reset({
        name: existingTemplate.name,
        description: existingTemplate.description || '',
        status: existingTemplate.status,
        questions: (existingTemplate.questions ?? []).map((q) => ({
          question_text: q.question_text,
          question_type: q.question_type,
          options: q.options,
          is_required: q.is_required,
          position: q.position,
        })),
        email_subject: existingTemplate.email_subject || '',
        email_body: existingTemplate.email_body || '',
        sender_name: existingTemplate.sender_name || '',
      });
    }
  }, [existingTemplate, isEditing, reset]);

  const onSubmit = async (data: TemplateFormData) => {
    const payload = {
      name: data.name,
      description: data.description,
      status: data.status,
      email_subject: data.email_subject,
      email_body: data.email_body,
      sender_name: data.sender_name,
      questions: data.questions.map((q, idx) => ({
        ...q,
        options: q.options && q.options.length > 0
          ? q.options.map((o) => o.trim()).filter(Boolean)
          : null,
        position: idx,
      })),
    };

    try {
      if (isEditing) {
        await updateTemplate.mutateAsync(payload);
      } else {
        await createTemplate.mutateAsync(payload);
      }
      navigate('/surveys/templates');
    } catch {
      // Error handled by React Query
    }
  };

  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback(
    (targetIndex: number) => {
      if (draggedIndex !== null && draggedIndex !== targetIndex) {
        move(draggedIndex, targetIndex);
      }
      setDraggedIndex(null);
    },
    [draggedIndex, move],
  );

  const addQuestion = () => {
    append({
      question_text: '',
      question_type: 'csat_score',
      options: null,
      is_required: true,
      position: fields.length,
    });
  };

  const needsOptions = (type: QuestionType) =>
    type === 'single_choice' || type === 'multi_choice';

  if (isLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Edit Template' : 'Create Template'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Basic Info */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Template Details</h2>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Name *
            </label>
            <input
              id="name"
              type="text"
              {...register('name')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="e.g. Q1 2024 Customer Satisfaction Survey"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              {...register('description')}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Brief description of this survey template"
            />
          </div>

          {isEditing && (
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="status"
                {...register('status')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          )}
        </div>

        {/* Questions */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Questions</h2>
            <button
              type="button"
              onClick={addQuestion}
              className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
            >
              <PlusIcon className="h-4 w-4" />
              Add Question
            </button>
          </div>

          {errors.questions?.root && (
            <p className="text-sm text-red-600">{errors.questions.root.message}</p>
          )}

          <div className="space-y-4">
            {fields.map((field, index) => (
              <QuestionCard
                key={field.id}
                index={index}
                register={register}
                control={control}
                errors={errors}
                watch={watch}
                remove={remove}
                move={move}
                totalQuestions={fields.length}
                needsOptions={needsOptions}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                isDragging={draggedIndex === index}
              />
            ))}
          </div>

          {fields.length > 3 && (
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={addQuestion}
                className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
              >
                <PlusIcon className="h-4 w-4" />
                Add Question
              </button>
            </div>
          )}
        </div>

        {/* Email Configuration */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Email Configuration</h2>
          <p className="text-sm text-gray-500">
            Configure the email subject and body for survey invitations.
          </p>

          <div>
            <label htmlFor="sender_name" className="block text-sm font-medium text-gray-700">
              Sender Name
            </label>
            <input
              id="sender_name"
              type="text"
              {...register('sender_name')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="CSAT Survey"
            />
          </div>

          <div>
            <label htmlFor="email_subject" className="block text-sm font-medium text-gray-700">
              Email Subject
            </label>
            <input
              id="email_subject"
              type="text"
              {...register('email_subject')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="We'd love your feedback!"
            />
          </div>

          <div>
            <label htmlFor="email_body" className="block text-sm font-medium text-gray-700">
              Email Body
            </label>
            <textarea
              id="email_body"
              {...register('email_body')}
              rows={5}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Hi {{contact_name}}, we'd appreciate it if you could take a moment to share your experience with us. Click the link below to complete the survey."
            />
            <p className="mt-1 text-xs text-gray-500">
              Available variables: {'{{contact_name}}'}, {'{{project_name}}'}, {'{{survey_link}}'}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/surveys/templates')}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : isEditing ? 'Update Template' : 'Create Template'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Question Card component

interface QuestionCardProps {
  index: number;
  register: UseFormRegister<TemplateFormData>;
  control: Control<TemplateFormData>;
  errors: FieldErrors<TemplateFormData>;
  watch: UseFormWatch<TemplateFormData>;
  remove: (index: number) => void;
  move: (from: number, to: number) => void;
  totalQuestions: number;
  needsOptions: (type: QuestionType) => boolean;
  onDragStart: (index: number) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (index: number) => void;
  isDragging: boolean;
}

function QuestionCard({
  index,
  register,
  control,
  errors,
  watch,
  remove,
  move,
  totalQuestions,
  needsOptions,
  onDragStart,
  onDragOver,
  onDrop,
  isDragging,
}: QuestionCardProps) {
  const questionType = watch(`questions.${index}.question_type`);
  const [optionsText, setOptionsText] = useState('');

  const currentOptions = watch(`questions.${index}.options`);

  useEffect(() => {
    if (currentOptions && Array.isArray(currentOptions)) {
      setOptionsText(currentOptions.join('\n'));
    }
  }, [currentOptions]);

  return (
    <div
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={onDragOver}
      onDrop={() => onDrop(index)}
      className={`border rounded-lg p-4 space-y-3 transition-all ${
        isDragging ? 'opacity-50 border-indigo-400 bg-indigo-50' : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bars3Icon className="h-5 w-5 text-gray-400 cursor-grab" />
          <span className="text-sm font-medium text-gray-700">Question {index + 1}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => move(index, Math.max(0, index - 1))}
            disabled={index === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            title="Move up"
          >
            <ArrowUpIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => move(index, Math.min(totalQuestions - 1, index + 1))}
            disabled={index === totalQuestions - 1}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            title="Move down"
          >
            <ArrowDownIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => remove(index)}
            disabled={totalQuestions === 1}
            className="p-1 text-red-400 hover:text-red-600 disabled:opacity-30"
            title="Remove question"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">
            Question Text *
          </label>
          <input
            type="text"
            {...register(`questions.${index}.question_text`)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="Enter your question..."
          />
          {errors.questions?.[index]?.question_text && (
            <p className="mt-1 text-sm text-red-600">
              {errors.questions[index]?.question_text?.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Type</label>
          <select
            {...register(`questions.${index}.question_type`)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            {questionTypes.map((qt) => (
              <option key={qt.value} value={qt.value}>
                {qt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2 pt-6">
          <input
            type="checkbox"
            id={`required-${index}`}
            {...register(`questions.${index}.is_required`)}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <label htmlFor={`required-${index}`} className="text-sm text-gray-700">
            Required
          </label>
        </div>
      </div>

      {needsOptions(questionType as QuestionType) && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Options (one per line)
          </label>
          <Controller
            control={control}
            name={`questions.${index}.options`}
            render={({ field }) => (
              <textarea
                value={optionsText}
                onChange={(e) => {
                  setOptionsText(e.target.value);
                }}
                onBlur={() => {
                  const opts = optionsText
                    .split('\n')
                    .map((o) => o.trim())
                    .filter(Boolean);
                  field.onChange(opts.length > 0 ? opts : null);
                }}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Option 1&#10;Option 2&#10;Option 3"
              />
            )}
          />
        </div>
      )}
    </div>
  );
}
